import React from "react";
import { ISearchParameter } from "../interface/iSearchParameter";
import { ISearchResults } from "../interface/iSearchResults";
import axios from "axios";
import { IAppointmentFilter } from "../interface/iAppointmentFilter";
import { User, AppContext } from "../../../../context/appContext";
import { endpoints } from "../../../../endpoints/endpoints";
import { IAppointment } from "../interface/iAppointment";

export const AnalyticsContext = React.createContext<any>({});

const AnalyticsProvider = ({ children }: any): React.JSX.Element => {
  const {
    user,
    checkSession,
  }: {
    user: User;
    checkSession: (response: any) => void;
  } = React.useContext(AppContext);

  const [searchParameter, setSearchParameter] =
    React.useState<ISearchParameter>({
      coach: null,
      fromDate: new Date().toISOString().split("T")[0],
      tillDate: new Date().toISOString().split("T")[0],
    });

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [searchResults, setSearchResults] = React.useState<ISearchResults>(
    {} as ISearchResults
  );

  const [appointmentFilter, setAppointmentFilter] =
    React.useState<IAppointmentFilter>({
      active: true,
      completed: false,
      free: false,
      missed: true,
      cancelled: true,
      searchString: "",
    });

  const handleSearch = React.useCallback((): void => {
    setIsLoading(true);

    axios
      .post(endpoints.analytics.coachAnalytics, {
        sessionId: user.sessionId,
        coachId: searchParameter.coach?.value,
        fromDate: searchParameter.fromDate,
        tillDate: searchParameter.tillDate,
      })
      .then((response) => {
        checkSession(response);

        console.log("Search results:", response.data);
        setSearchResults(response.data);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching coach list", error);
        setSearchResults({} as ISearchResults);
        setIsLoading(false);
      });
  }, [
    checkSession,
    searchParameter.coach?.value,
    searchParameter.fromDate,
    searchParameter.tillDate,
    user.sessionId,
  ]);

  const getFilteredAppointmentList = React.useCallback((): IAppointment[] => {
    // searchString is empty or lastName OR firstName includes searchString
    const filterForName = (appointment: IAppointment) =>
      appointmentFilter.searchString === "" ||
      (appointment.lastName &&
        appointment.lastName
          .toLowerCase()
          .includes(appointmentFilter.searchString.toLowerCase())) ||
      (appointment.firstName &&
        appointment.firstName
          .toLowerCase()
          .includes(appointmentFilter.searchString.toLowerCase()));

    // active
    const filterActive = (appointment: IAppointment) =>
      appointment.status === "G" && new Date(appointment.date) >= new Date();

    // completed
    const filterCompleted = (appointment: IAppointment) =>
      appointment.status === "G" && new Date(appointment.date) < new Date();

    // free
    const filterFree = (appointment: IAppointment) =>
      appointment.status === "F";

    // missed
    const filterMissed = (appointment: IAppointment) =>
      appointment.status === "V";

    // cancelled
    const filterCancelled = (appointment: IAppointment) =>
      appointment.status === "K";

    // Filter appointments based on the selected filters
    const filteredAppointments =
      searchResults.appointmentList.appointments.filter(
        (appointment: IAppointment) =>
          filterForName(appointment) &&
          ((appointmentFilter.active && filterActive(appointment)) ||
            (appointmentFilter.completed && filterCompleted(appointment)) ||
            (appointmentFilter.free && filterFree(appointment)) ||
            (appointmentFilter.missed && filterMissed(appointment)) ||
            (appointmentFilter.cancelled && filterCancelled(appointment)))
      );

    // Sort appointments by date and time
    filteredAppointments.sort(
      (appointmentA: IAppointment, appointmentB: IAppointment) => {
        const dateA = new Date(
          appointmentA.date + " " + appointmentA.startTime
        );

        const dateB = new Date(
          appointmentB.date + " " + appointmentB.startTime
        );

        return dateA.getTime() - dateB.getTime();
      }
    );

    return filteredAppointments;
  }, [appointmentFilter, searchResults]);

  const value: {} = React.useMemo(
    () => ({
      searchParameter,
      setSearchParameter,
      isLoading,
      setIsLoading,
      searchResults,
      setSearchResults,
      handleSearch,
      appointmentFilter,
      setAppointmentFilter,
      getFilteredAppointmentList,
    }),
    [
      searchParameter,
      isLoading,
      searchResults,
      handleSearch,
      appointmentFilter,
      getFilteredAppointmentList,
    ]
  );

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export default AnalyticsProvider;
